import { type FunctionComponent, useState } from 'react';
import { MAX_PAGE_WIDTH } from '../../../../constants/breakpoints';
import { HOMEOWNERS_TAB_DATA, PROS_TAB_DATA, TOOLS_DESKTOP_IMAGES, TOOLS_MOBILE_IMAGES } from '../../../../constants/project/about-page';
import { buildGTMProjectInteractionTracking } from '../../../../helpers/analytics/gtm/gtm-utils.helper';
import { useTrackEvent } from '../../../../hooks/analytics/analytics.hooks';
import { type FaqTabData, type ToolsTabData } from '../../../../types/project-about.types';
import { Tabs, Tab } from '../../../common-components/tabs/tabs.component';
import { CloudinaryImage } from '../../../images/cloudinary-image/cloudinary-image.component';
import { GetStartedButton } from '../get-started-button/get-started-button.component';
import { ProjectsAccordion } from '../projects-accordion/projects-accordion.component';
import { ProjectsTourButton } from '../projects-tour-button/projects-tour-button.component';
import { toolsImage, toolsSectionHeight } from './about-page-tools.css';

type TabNames = 'Homeowners' | 'Pros';

const TabTitle: FunctionComponent<{ section: TabNames }> = ({ section }) => (
	<p className="ma0">
		<span className="dn di-ns">For </span>
		{section === 'Homeowners' ? <>Homeowners</> : <>Pros</>}
	</p>
);

export const AboutPageTools: FunctionComponent = () => {
	const track = useTrackEvent();
	const [activeTab, setActiveTab] = useState<TabNames>('Homeowners');

	const [openTabIndex, setOpenTabIndex] = useState<number>(0);

	const currentData = activeTab === 'Homeowners' ? HOMEOWNERS_TAB_DATA : PROS_TAB_DATA;
	const tabClasses = 'br2 theme-primary-dark pv2 w-100 tc theme-primary f3 font-medium';
	const tabActiveClasses = `${tabClasses} bg-theme-primary-lighter`;

	const getTitle = (item: ToolsTabData | FaqTabData): string => ('name' in item ? item.name : 'No data available');
	const getContent = (item: ToolsTabData | FaqTabData): JSX.Element => {
		if ('name' in item) {
			const { name, description, bulletpoints } = item;
			return (
				<>
					<p className="f5 theme-grey-dark mv0 lh-copy">{description}</p>
					<ul className="mt0 pl5">
						{bulletpoints.map((bulletpoint, idx) => (
							<li key={`${name}-bulletpoint-${idx}`} className="theme-grey-dark">
								{bulletpoint}
							</li>
						))}
					</ul>
				</>
			);
		}
		return <p>No data available</p>;
	};

	const onTabChange = (tabName: string) => {
		if (tabName === 'Homeowners' || tabName === 'Pros') {
			setActiveTab(tabName);
		}
		track(buildGTMProjectInteractionTracking('projects-about-page', `${tabName}-tab`));
	};

	const onClickDropdown = (tabName: string, index: number, title: string) => {
		setOpenTabIndex(index);
		track(buildGTMProjectInteractionTracking('projects-about-page', `${tabName}-${title}-Dropdown`));
	};

	return (
		<section className={`relative flex flex-column items-center w-100 pv7-ns ph3-ns ${toolsSectionHeight}`}>
			<div className="flex flex-column flex-row-ns w-100 z-2" style={{ maxWidth: MAX_PAGE_WIDTH }}>
				<div className="relative dn db-ns w-100 mr6 mt2" style={{ height: 475 }}>
					{Object.entries(TOOLS_DESKTOP_IMAGES).map(([key, publicId], index) => (
						<CloudinaryImage
							key={key}
							publicID={publicId}
							className={`absolute h-100 ${toolsImage} ${openTabIndex === index ? 'o-100 z-1' : ''} ${
								openTabIndex !== index && index !== 0 ? 'o-0' : ''
							}`}
							description={`${key} with projects`}
							options={{ crop: 'fit' }}
						/>
					))}
				</div>
				<div className="flex dn-ns justify-center w-100 mb5">
					<div className="relative" style={{ width: 343, height: 480 }}>
						{Object.entries(TOOLS_MOBILE_IMAGES).map(([key, publicId], index) => (
							<CloudinaryImage
								key={key}
								publicID={publicId}
								className={`absolute ${toolsImage} ${openTabIndex === index ? 'o-100 z-1' : ''} ${
									openTabIndex !== index && index !== 0 ? 'o-0' : ''
								}`}
								description={`${key} with projects`}
								options={{ crop: 'fit' }}
							/>
						))}
					</div>
				</div>
				<div className="w-100 ph3 ph0-ns mb5">
					<h2 className="mh0 mt4 mt0-ns mb3 pa0 f2 font-medium">Tools for Everyone</h2>
					<div className="br2 shadow-3 bg-theme-white" style={{ maxWidth: 584 }}>
						<Tabs
							tabStyle="NONE"
							startingTabName="Homeowners"
							tabListClasses=""
							tabControlClasses="br2 mb2 pa2 bb-0"
							onActiveTabChanged={onTabChange}>
							<Tab
								automationHook="homeowners"
								icon={<TabTitle section="Homeowners" />}
								name="Homeowners"
								tabClasses={tabClasses}
								tabActiveClasses={`${tabActiveClasses} ph5 ph0-ns`}
							/>
							<Tab
								automationHook="pros"
								icon={<TabTitle section="Pros" />}
								name="Pros"
								tabClasses={tabClasses}
								tabActiveClasses={tabActiveClasses}
							/>
						</Tabs>
						<ProjectsAccordion
							data={currentData}
							getContent={getContent}
							getTitle={getTitle}
							onTabClick={(index, title) => onClickDropdown(activeTab, index, title)}
						/>
					</div>
					<div className="flex gc3 mt4">
						<GetStartedButton />
						<ProjectsTourButton />
					</div>
				</div>
			</div>
		</section>
	);
};
